import { isArray } from 'lodash-es';
import classNames from 'classnames';
import { playAudio, parsingAI } from '@/utils/audioPlay';
import { IObjNumber, IPictureCompletionProp, IlistItem } from '@/utils/ctype';
import Input from '../../../components/MyInput';
import HtmlParser from '../../../components/HtmlParser';
import style from './index.module.less';

const leadingEnSpacesPattern = /^\u2002+/;
const labelledLinePattern = /^(<b>.*?<\/b>)(\u2002+)(.*)$/;

const splitLeadingIndent = (text: string) => {
  const leadingSpaces = text.match(leadingEnSpacesPattern)?.[0] || '';

  return {
    content: text.slice(leadingSpaces.length),
    leadingSpaces,
    paddingInlineStart: leadingSpaces ? `${leadingSpaces.length * 0.5}em` : undefined,
  };
};

const splitLabelledLine = (text: string) => {
  const match = text.match(labelledLinePattern);

  return match ? {label: match[1], indent: match[2], content: match[3]} : undefined;
};

// type 5

export default function SentenceCompletion(props: IPictureCompletionProp) {
  const { data, saveAnswerHandle, groupId, type, answerMap, isAI, isListen, keyIndex, answerAllText = [], setFocusIndexHandle, feedbackMap, displayAnswerMap } = props;
  const { list } = data;
  const listIdToNumberMap: IObjNumber = {};
  const listMap :IObjNumber = {};
  list.forEach(( item, index: number) => {
    listIdToNumberMap[item.id] = item.number;
    listMap[item.id] = index;
  });

  const handleInput = (event: React.FormEvent<HTMLInputElement>, id: string, itemIndex: number) => {
    const lastValue = answerMap[`${groupId}_${type}_${id}`] ? [...answerMap[`${groupId}_${type}_${id}`]] : [];
    const value = (event.target as HTMLInputElement).value ;
    lastValue[itemIndex] = value;
    saveAnswerHandle(listIdToNumberMap[id], {
      group_id: groupId,
      id: Number(id),
      type,
      answer: lastValue,
    });
  };

  const createAIType = (answer: string, listItem: IlistItem) => {
    if (!isAI) {
      return;
    }
    if (feedbackMap && listItem.id) {
      return feedbackMap[listItem.id];
    }
  };

  const replacePlaceholder = (str: string) => {
    // 正则表达式匹配 $数字$
    const regex = /\$(\d+)\$/g;
    const parts = str.split(regex);
    const partMap: { [key: string]: number; } = {};

    const partsCount: { [key: string]: number; } = {};
    parts.forEach((part, index) => {
      if (index % 2 === 1) {
        if (!partsCount[part]) {
          partsCount[part] = 1;
        } else {
          partsCount[part]++;
        }
      }
    });
    
    const elements = parts.map((part, index) => {
      
      // parts 中的奇数索引是数字，偶数索引是普通文本
      if (index % 2 === 1) {
        const itemIndex = typeof partMap[part] === 'number' ? partMap[part] + 1 : 0;
        partMap[part] = itemIndex;
        const value = answerMap[`${groupId}_${type}_${part}`] && answerMap[`${groupId}_${type}_${part}`][itemIndex];
        const listIndex = listMap[part];
        const item = list && list[listIndex];
        const number = item?.number;
        const answer = isArray(answerAllText[number - 1]) ? answerAllText[number - 1][itemIndex] : answerAllText[number - 1];
        const display_answerHtml = displayAnswerMap?.[item.id] || item?.display_answerHtml;
        return <Input 
          className={classNames(`question${number}`, style.sentenceInput)}
          hideCorrect={(itemIndex + 1 < partsCount[part])}
          numberKey={`$question.question.${keyIndex}.list.${listIndex}.number`}
          numberHtml={item.numberHtml}
          dataKey={`question.question.${keyIndex}.list.${listIndex}.display_answerHtml`}
          displayAnswer={display_answerHtml}  
          answerKey={`answerAllText.${number - 1}`} 
          answer={answer}
          type={createAIType(value, list[listIndex])} 
          key={number} 
          value={value} 
          placeholder={String(number)}
          id={`question${number}_${itemIndex}`} 
          onInput={(event) => {handleInput(event, part, itemIndex);}} 
          playAudio={() => {playAudio(item);}} 
          parsingAI={() => {parsingAI(item);}} 
          isListen={isListen}
          onFocus={()=>{setFocusIndexHandle&&setFocusIndexHandle(number);}}></Input>;
      } else {
        return <HtmlParser htmlString={part} key={`p_${index}`}></HtmlParser>;
      }
    });
    return elements;
  };


  const TransportSurvey = (text: string ) => {


    const textArr = text.split('\n');
    const labelledRows = textArr.map(splitLabelledLine);
    const usesLabelColumns = labelledRows.some(Boolean);

    if (usesLabelColumns) {
      return (
        <div className={style.noteGrid} role="table">
          {textArr.map((itemText: string, index) => {
            const labelledRow = labelledRows[index];
            const {content, paddingInlineStart, leadingSpaces} = splitLeadingIndent(itemText);
            const sourceGap = labelledRow ? labelledRow.indent.length : leadingSpaces.length;

            if (labelledRow || paddingInlineStart) {
              return (
                <div className={style.noteRow} role="row" key={index}>
                  <div className={style.noteLabel} role="cell">
                    {labelledRow ? <HtmlParser htmlString={labelledRow.label}></HtmlParser> : null}
                  </div>
                  <div className={style.completion} role="cell" data-source-gap={sourceGap || undefined}>
                    {replacePlaceholder(labelledRow ? labelledRow.content : content)}
                  </div>
                </div>
              );
            }

            return (
              <div className={style.noteRow} role="row" key={index}>
                <div className={classNames(style.completion, style.fullRow)} role="cell">
                  {itemText ? replacePlaceholder(itemText) : <br />}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return textArr.map((itemText: string, index) => {
      if (itemText) {
        const {content, paddingInlineStart, leadingSpaces} = splitLeadingIndent(itemText);
        return (
          <div
            className={style.completion}
            key={index}
            style={paddingInlineStart ? {paddingInlineStart} : undefined}
            data-source-gap={leadingSpaces.length || undefined}
          >
            {replacePlaceholder(content)}
          </div>
        );
      } else {
        return (
          <div key={index}>
            <br />
          </div>
        );
      }
      
    });
  };
  return (
    <div data-key={`question.question.${keyIndex}.collect`}>
      {TransportSurvey(data.collect)}
    </div>
  );
}
